import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PeopleIcon from "@mui/icons-material/People";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import useApiEp from "../../hooks/useApiEp";
import { formatNumber } from "../../utils/util";

// Styled card with sharp, vibrant colors
const GlassyCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.15
  )} 0%, ${alpha(theme.palette.background.paper, 0.25)} 100%)`,
  backdropFilter: "blur(12px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  boxShadow: `0 8px 32px 0 ${alpha("#6A11CB", 0.3)}`,
  borderRadius: "16px",
  overflow: "hidden",
  transition: "all 0.3s ease-in-out",
  position: "relative",
  "&:before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: `linear-gradient(90deg, ${"#FF416C"}, ${"#FF4B2B"})`,
    opacity: 0.9,
  },
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: `0 12px 40px 0 ${alpha("#6A11CB", 0.4)}`,
  },
}));

// Styled icon container with vibrant gradients
const IconContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 56,
  height: 56,
  borderRadius: "12px",
  background: `linear-gradient(135deg, ${"#6A11CB"} 0%, ${"#2575FC"} 100%)`,
  color: theme.palette.common.white,
  marginRight: theme.spacing(2),
  boxShadow: `0 4px 12px ${alpha("#2575FC", 0.5)}`,
}));

const LeadsEnquiriesCard = ({ id }) => {
  const theme = useTheme();
  const { data: leadsData, isLoading: leadsLoading } = useApiEp(
    `/root/get-leads-count-for-account/${id}`
  );
  const { data: enquiriesData, isLoading: enquiriesLoading } = useApiEp(
    `/root/get-enquiries-count-for-account/${id}`
  );

  return (
    <Grid container spacing={3} justifyContent="center">
      {/* Leads Card */}
      <Grid item xs={12} sm={6} md={4}>
        <GlassyCard>
          <CardContent
            sx={{ display: "flex", alignItems: "center", padding: 3 }}
          >
            <IconContainer
              sx={{
                background: `linear-gradient(135deg, ${"#FF416C"} 0%, ${"#FF4B2B"} 100%)`,
                boxShadow: `0 4px 12px ${alpha("#FF4B2B", 0.5)}`,
              }}
            >
              <PeopleIcon fontSize="medium" />
            </IconContainer>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "#2D3748",
                }}
              >
                Total Leads
              </Typography>
              {leadsLoading ? (
                <CircularProgress
                  size={28}
                  thickness={4}
                  sx={{ color: "#FF416C" }}
                />
              ) : (
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  sx={{
                    color: "#6A11CB",
                    textShadow: `0 2px 4px ${alpha("#6A11CB", 0.3)}`,
                  }}
                >
                  {(leadsData?.count ?? 0).toLocaleString()}
                </Typography>
              )}
            </Box>
          </CardContent>
        </GlassyCard>
      </Grid>

      {/* Enquiries Card */}
      <Grid item xs={12} sm={6} md={4}>
        <GlassyCard>
          <CardContent
            sx={{ display: "flex", alignItems: "center", padding: 3 }}
          >
            <IconContainer
              sx={{
                background: `linear-gradient(135deg, ${"#00CDAC"} 0%, ${"#02AAB0"} 100%)`,
                boxShadow: `0 4px 12px ${alpha("#02AAB0", 0.5)}`,
              }}
            >
              <QuestionAnswerIcon fontSize="medium" />
            </IconContainer>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "#2D3748",
                }}
              >
                Total Enquiries
              </Typography>
              {enquiriesLoading ? (
                <CircularProgress
                  size={28}
                  thickness={4}
                  sx={{ color: "#00CDAC" }}
                />
              ) : (
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  sx={{
                    color: "#0575E6",
                    textShadow: `0 2px 4px ${alpha("#0575E6", 0.3)}`,
                  }}
                >
                  {(enquiriesData?.count ?? 0).toLocaleString()}
                </Typography>
              )}
            </Box>
          </CardContent>
        </GlassyCard>
      </Grid>
    </Grid>
  );
};

export default LeadsEnquiriesCard;
